/*!
  Pioneer WFRIA Anchor v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["Anchor"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("Anchor") < 0) {
    window.__PIONEER_GLOBAL__.components.push("Anchor");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["disabled", "className", "kind", "centerIcon", "href", "tel", "rel", "ceoMobileCompat"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { IconChildWindow, IconPhone } from "../";
import { invalidIf, requiredIf, validIcon } from "../common/proptypes";
import BaseButton from "../private/BaseButton/BaseButton";
import { visuallyHidden } from "../themes/common/styles";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const TEL_DEFAULTS = {
  icon: false,
  iconPosition: "left",
  value: null
};
const BUTTON_STYLES = process.env.NODE_ENV !== "production" ? ["primary", "standard", "chromeless", "simple"] : {};
const NEW_WINDOW_TARGET = "_blank";

function addQueryStrings(href) {
  const NEW_WINDOW_FLAG_FOR_MOBILE = "nxnewwindow";
  let url = href;

  if (href.indexOf("?") === -1) {
    url = `${href}?${NEW_WINDOW_FLAG_FOR_MOBILE}=true`;
  } else if (href.indexOf(NEW_WINDOW_FLAG_FOR_MOBILE) === -1) {
    url = `${href}&${NEW_WINDOW_FLAG_FOR_MOBILE}=true`;
  }

  return url;
}

const Anchor = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    disabled,
    className,
    kind: kindProp,
    centerIcon,
    href: hrefProp,
    tel: telProp,
    rel: relProp,
    ceoMobileCompat
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  let telLeftIcon, telRightIcon;
  const theme = useTheme();
  const {
    cssRoot,
    cssIcon
  } = extractTheme({
    theme,
    kind: kindProp
  });

  const tel = _objectSpread(_objectSpread({}, TEL_DEFAULTS), telProp);

  const kind = tel.value ? "simple" : kindProp;
  const ceoMobileCompatHref = ceoMobileCompat ? addQueryStrings(hrefProp) : hrefProp;
  const href = tel.value ? `tel:${tel.value}` : ceoMobileCompatHref;
  const anchorLabel = centerIcon ? null : otherProps.children || href;
  const tooltipLabel = otherProps["aria-label"] || anchorLabel || "Tooltip";
  const rel = otherProps.target === "_blank" ? `${relProp} noopener noreferrer`.trim() : relProp;
  /* istanbul ignore next */

  const openInChildWindow = kind === "simple" && href && otherProps.target && otherProps.target === NEW_WINDOW_TARGET ? _jsxs(React.Fragment, {
    children: [_jsx("span", {
      css: visuallyHidden,
      children: ", opens in a new window"
    }), _jsx(IconChildWindow, {
      css: cssIcon,
      decorative: true
    })]
  }) : null;
  /* istanbul ignore next */

  if (tel.value && tel.icon) {
    if (tel.iconPosition === "right") {
      telRightIcon = _jsx(IconPhone, {
        css: cssIcon,
        decorative: true
      });
    } else {
      telLeftIcon = _jsx(IconPhone, {
        css: cssIcon,
        decorative: true
      });
    }
  }

  const radius = cssRoot.borderRadius;
  /* istanbul ignore next */

  return _jsx(BaseButton, _objectSpread({
    radius: radius,
    href: disabled ? null : href,
    kind: kind,
    elementType: "a",
    role: disabled ? "link" : null,
    disabled: disabled,
    "aria-disabled": disabled ? "true" : null,
    ref: ref,
    rel: rel === "" ? null : rel,
    anchorLabel: anchorLabel,
    anchorClassName: className,
    tooltipLabel: tooltipLabel,
    telLeftIcon: telLeftIcon,
    telRightIcon: telRightIcon,
    centerIcon: centerIcon,
    openInChildWindow: openInChildWindow
  }, otherProps));
});
Anchor.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Stretch the anchor to take the full width of its container up to 300px. */
  block: PropTypes.bool,

  /**
   * An SVGIcon component to create an icon-only-button. When used,
   * children are ignored. For accessibility reasons, you need
   * to also provide an aria-label.
   */
  centerIcon: validIcon,

  /**
   * Adds query strings to links to help CEO mobile handle a limitation
   * on the number of tabs opened in one session. Use this prop if your
   * application is available in the CEO mobile app.
   */
  ceoMobileCompat: PropTypes.bool,

  /** The body of the anchor. */
  children: requiredIf(PropTypes.node, props => !props.children && !props.href, "when 'href' is not supplied."),
  className: PropTypes.string,

  /**
   * Disable the anchor -> makes it a disabled button.
   */
  disabled: PropTypes.bool,

  /** The URL to link to. */
  href: requiredIf(PropTypes.string, props => !props.children && !props.href, "when 'children' is not supplied."),

  /** The style of the anchor. */
  kind: PropTypes.oneOf(BUTTON_STYLES),

  /** An SVGIcon component to place on the left of the anchor. */
  leftIcon: invalidIf(validIcon, (props, propName) => props[propName] !== null && props.tel.icon, `when used with the tel icon also visible.`),

  /**
   * The rel attribute defines the relationship between a linked
   * resource and the current document.
   */
  rel: PropTypes.string,

  /**
   * Span 100% width of the parent at lower breakpoint when used without an icon.
   * When combined with an icon, the anchor does not extend, but the label gets
   * hidden at lower breakpoints.
   */
  responsive: PropTypes.bool,

  /** An SVGIcon component to place on the right of the anchor. */
  rightIcon: invalidIf(validIcon, (props, propName) => props[propName] !== null && props.tel.icon, `when used with the tel icon also visible.`),

  /** Smaller profile. */
  slim: PropTypes.bool,

  /**
   * Configuration for displaying an actionable phone number (click-to-call).
   */
  tel: PropTypes.shape({
    /**
     * Phone number with optional extension.
     * `Default: null`
     */
    value: PropTypes.string,

    /**
     * Configuration to show/hide the phone icon.
     * `Default: false`
     */
    icon: PropTypes.bool,

    /**
     * Configuration to place the phone icon to the left/right of the number/text.
     * `Default: "left"`
     */
    iconPosition: PropTypes.oneOf(["left", "right"])
  })
} : {};
Anchor.defaultProps = {
  "aria-label": null,
  centerIcon: null,
  children: null,
  className: "",
  block: false,
  disabled: false,
  href: null,
  kind: "simple",
  leftIcon: null,
  rel: "",
  responsive: false,
  rightIcon: null,
  slim: false,
  tel: TEL_DEFAULTS,
  ceoMobileCompat: false
};
Anchor.displayName = "Anchor";
export default Anchor;